import { useState } from 'preact/hooks';
import { get, media, post } from '../api';
import { me } from '../session';
import { startCheckout } from '../billing';
import { Button, Card, Empty, Link, Loading, money, navigate, Pill, toast, useAction, useLoad } from '../ui';

function TemplateCard(p: { t: any }) {
	const t = p.t;
	return (
		<Link href={`/templates/${t.id}`} class="game-card">
			<div class="game-cover" style={t.cover ? { backgroundImage: `url(${media(t.cover, 640)})` } : undefined}>
				<Pill tone={t.priceCents ? 'accent' : 'ok'}>{money(t.priceCents)}</Pill>
			</div>
			<div class="game-body">
				<h3>{t.title}</h3>
				<p class="muted small">by {t.author}</p>
				<p class="small clamp">{t.description}</p>
				<div class="game-meta">
					<span>
						{t.minPlayers}–{t.maxPlayers} players
					</span>
					{t.length ? <span>{t.length}</span> : null}
					<span>{t.rating}</span>
					<span>{t.stars ? `★ ${t.stars} (${t.ratingCount})` : 'unrated'}</span>
					<span>played {t.plays}×</span>
				</div>
			</div>
		</Link>
	);
}

export function Templates() {
	const m = me.use();
	const [q, setQ] = useState('');
	const [free, setFree] = useState(false);
	const qs = new URLSearchParams({ ...(q ? { q } : {}), ...(free ? { free: '1' } : {}) }).toString();
	const list = useLoad(() => get(`/api/templates?${qs}`), [qs]);
	const mine = useLoad(() => (m?.user ? get('/api/templates/mine') : Promise.resolve(null)), [m?.user?.id]);
	return (
		<main class="page">
			<h1>Templates</h1>
			<p class="lede">Worlds other GMs have published. Add one to your library and start a game with it.</p>
			<Card>
				<div class="filters">
					<input type="search" placeholder="Search templates" value={q} onInput={(e) => setQ(e.currentTarget.value)} aria-label="Search templates" />
					<label class="check">
						<input type="checkbox" checked={free} onChange={(e) => setFree(e.currentTarget.checked)} /> Free only
					</label>
				</div>
			</Card>
			{!list.data ? <Loading /> : list.data.templates.length ? (
				<div class="grid-cards">
					{list.data.templates.map((t: any) => (
						<TemplateCard key={t.id} t={t} />
					))}
				</div>
			) : (
				<Empty>No templates match.</Empty>
			)}
			{mine.data ? (
				<div class="two-col">
					<Card title="Your library">
						{mine.data.library.length ? (
							<ul class="list">
								{mine.data.library.map((l: any) => (
									<li key={l.templateId} class="row-between">
										<span>
											<Link href={`/templates/${l.templateId}`}>{l.title}</Link> <Pill>{l.via}</Pill>
										</span>
										{l.latestVersion > l.version ? (
											<Button
												small
												variant="ghost"
												onClick={async () => {
													await post(`/api/templates/${l.templateId}/pull`);
													toast('New games will start from the latest version.', 'ok');
													mine.reload();
												}}
											>
												Update to v{l.latestVersion}
											</Button>
										) : (
											<span class="small muted">v{l.version}</span>
										)}
									</li>
								))}
							</ul>
						) : (
							<Empty>Nothing yet.</Empty>
						)}
					</Card>
					<Card title="Published by you">
						{mine.data.authored.length ? (
							<ul class="list">
								{mine.data.authored.map((t: any) => (
									<li key={t.id}>
										<Link href={`/templates/${t.id}`}>{t.title}</Link> <Pill tone={t.status === 'listed' ? 'ok' : t.status === 'rejected' ? 'bad' : 'accent'}>{t.status.replace('_', ' ')}</Pill> <span class="small muted">v{t.version} · {money(t.priceCents)}</span>
									</li>
								))}
							</ul>
						) : (
							<Empty>Nothing published.</Empty>
						)}
					</Card>
				</div>
			) : null}
		</main>
	);
}

export function TemplateDetail(p: { id: string }) {
	const m = me.use();
	const d = useLoad(() => get(`/api/templates/${p.id}`), [p.id]);
	const [acquire, busy] = useAction(async () => {
		try {
			await post(`/api/templates/${p.id}/acquire`);
			toast('Added to your library.', 'ok');
			d.reload();
		} catch (e: any) {
			if (e.data?.needsPurchase) {
				if (await startCheckout({ kind: 'template', templateId: p.id })) {
					await post(`/api/templates/${p.id}/acquire`);
					d.reload();
				}
			} else throw e;
		}
	});
	const [rate] = useAction(async (stars: number) => {
		await post(`/api/templates/${p.id}/rate`, { stars });
		toast('Thanks for rating.', 'ok');
		d.reload();
	});
	if (d.error) return <main class="page"><Card title="Not found"><p>{d.error}</p></Card></main>;
	if (!d.data) return <Loading />;
	const t = d.data.template;
	const pv = d.data.preview;
	return (
		<main class="page">
			<div class="template-hero" style={t.cover ? { backgroundImage: `url(${media(t.cover, 1600)})` } : undefined}>
				<div>
					<h1>{t.title}</h1>
					<p>
						by {t.author} · {t.minPlayers}–{t.maxPlayers} players · {t.length} · {t.rating} · {t.stars ? `★ ${t.stars}` : 'unrated'} · played {t.plays}×
					</p>
					{d.data.isAuthor ? (
						<p>
							<Pill tone={t.status === 'listed' ? 'ok' : 'accent'}>{t.status.replace('_', ' ')}</Pill> {d.data.reviewNote ? <span class="small">Review note: {d.data.reviewNote}</span> : null}
						</p>
					) : null}
				</div>
				<div>
					{m?.user ? (
						d.data.owned ? (
							<Button onClick={() => navigate('/create')}>Start a game</Button>
						) : (
							<Button onClick={() => acquire()} busy={busy}>
								{t.priceCents ? `Buy for ${money(t.priceCents)}` : 'Add to my library'}
							</Button>
						)
					) : (
						<Link href="/">Sign in to get it</Link>
					)}
				</div>
			</div>
			<div class="two-col">
				<Card title="About">
					<p>{t.description}</p>
					<p class="small muted">{pv.tone}</p>
					<h4>It begins…</h4>
					<ul class="list">
						{pv.openingArcs.map((a: any) => (
							<li key={a.name}>
								<strong>{a.name}</strong>: {a.opening} — {a.description}
							</li>
						))}
					</ul>
					{pv.publicFacts.length ? (
						<>
							<h4>Common knowledge</h4>
							<ul class="list small">
								{pv.publicFacts.map((f: string) => (
									<li key={f}>{f}</li>
								))}
							</ul>
						</>
					) : null}
					<p class="small muted">{pv.areas} areas. Previews never show secrets, later arc stages or endings.</p>
				</Card>
				<Card title="Playable characters">
					<div class="char-grid">
						{pv.playable.map((c: any) => (
							<div key={c.name} class="char-card">
								{c.asset ? <img src={media(c.asset, 320)} alt="" /> : null}
								<h3>{c.name}</h3>
								<p class="muted small">{c.role}</p>
								<p class="small">{c.description}</p>
							</div>
						))}
					</div>
				</Card>
			</div>
			{d.data.owned && !d.data.isAuthor ? (
				<Card title="Rate it (after you've played)">
					<div class="row">
						{[1, 2, 3, 4, 5].map((s) => (
							<button key={s} class={`star ${d.data.myRating >= s ? 'star-on' : ''}`} onClick={() => rate(s)} aria-label={`${s} stars`}>
								★
							</button>
						))}
					</div>
				</Card>
			) : null}
			{d.data.versions.length > 1 ? (
				<Card title="Versions">
					<ul class="list small">
						{d.data.versions.map((v: any) => (
							<li key={v.version}>
								v{v.version} — {v.notes || 'no notes'} ({new Date(v.created_at).toLocaleDateString()})
							</li>
						))}
					</ul>
				</Card>
			) : null}
		</main>
	);
}
